import { useEffect, useState } from "react";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";
import { DatePicker } from "@/components/ui/date-picker";
import { TimePicker } from "@/components/ui/time-picker";
import { formatDuration } from "@/lib/api";
import { toDateStr, toTimeStr, parseDateTime } from "./utils";

/**
 * Popover that lets the user edit the *start* time/date of a running timer.
 * The end stays fixed at "now" (the timer is live), so editing the start
 * moves the elapsed counter accordingly.
 */
export default function RunningStartPopover({
  open, onOpenChange, runningEntry, onSaveStart, children,
}) {
  const seed = runningEntry ? new Date(runningEntry.start) : new Date();
  const [date, setDate] = useState(toDateStr(seed));
  const [timeStr, setTimeStr] = useState(toTimeStr(seed));

  useEffect(() => {
    if (open && runningEntry) {
      const d = new Date(runningEntry.start);
      setDate(toDateStr(d));
      setTimeStr(toTimeStr(d));
    }
  }, [open, runningEntry]);

  const newStartDt = parseDateTime(date, timeStr);
  const now = new Date();
  const invalid = newStartDt > now;
  const elapsedSec = Math.max(0, Math.floor((now - newStartDt) / 1000));

  const handleSave = () => {
    if (invalid) return;
    onSaveStart(newStartDt.toISOString());
    onOpenChange(false);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-[380px] p-0" align="end" data-testid="running-start-popover">
        <div className="p-4 border-b border-slate-100">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">Edit start</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">Start time</div>
              <TimePicker
                value={timeStr}
                onChange={setTimeStr}
                className="w-full"
                testId="running-start-time-input"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">Date</div>
              <DatePicker
                value={date}
                onChange={setDate}
                className="w-full"
                testId="running-start-date-input"
              />
            </div>
          </div>
        </div>

        <div className="p-4 border-b border-slate-100 flex items-center justify-between text-xs">
          <div>
            <div className="text-slate-400 uppercase tracking-widest text-[10px] mb-1">Elapsed (preview)</div>
            <div className="timer-font text-lg text-slate-700 tabular-nums" data-testid="running-start-preview-elapsed">
              {formatDuration(elapsedSec)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-slate-400 uppercase tracking-widest text-[10px] mb-1">End</div>
            <div className="text-slate-400 italic">Running…</div>
          </div>
        </div>

        {invalid && (
          <div className="px-4 py-2 text-xs text-red-500 bg-red-50 border-b border-red-100"
               data-testid="running-start-error">
            Start cannot be in the future.
          </div>
        )}

        <div className="p-3 flex items-center justify-end gap-2">
          <button
            onClick={() => onOpenChange(false)}
            className="px-3 py-1.5 text-sm text-slate-500 hover:text-slate-900"
            data-testid="running-start-cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={invalid}
            className="px-4 py-1.5 text-sm text-white rounded-md disabled:opacity-40"
            style={{ background: "#145c8c" }}
            data-testid="running-start-save"
          >
            Save
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
